import "./BusinessUnderstanding.scss";

import * as Dfns from "date-fns/fp";
import * as R from "ramda";

import { DATE_GROUPING_OPTIONS, DateGroupingKey } from "../../MMM/ModelResults/ModelOverview";
import {
  DmaMap,
  DownloadDropdown,
  Dropdown,
  DropdownToggleType,
  InfoTooltip,
} from "../../Components";
import { Fit, MAPEByGeo, SummaryMetrics } from "@blisspointmedia/bpm-types/dist/GeoMMM";
import {
  OUTCOME_FORMATTER_TYPE,
  OUTCOME_TYPE_STRING,
  percentageToSplitFraction,
} from "../DataFormatters";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { dateToMddyy, getGroupingValue } from "../../utils/date-utils";
import { downloadJSONToCSV, downloadPNG, exportToExcel } from "../../utils/download-utils";
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDevNonAdjusted,
} from "../../BrandHealthMetrics/BrandHealthMetricsUtils";

import ActualVsPredicted from "./Components/ActualVsPredicted";
import AreaChart from "../../Components/Charts/AreaChart";
import AutoSizer from "react-virtualized-auto-sizer";
import { Brand20 } from "../../utils/colors";
import ChartContainer from "../../Components/ChartContainer";
import DynamicModelOverview from "../../Components/DynamicModelOverview/DynamicModelOverview";
import { MdDone } from "react-icons/md";
import ModelAccuracyGradient from "./Components/ModelAccuracyGradient";
import Spend from "./Components/Spend";
import WidgetContainer from "../../Components/WidgetContainer";
import { cptbAreaData } from "../../MMM/ModelResults/DataFormatters";
import { currencyFormatter } from "../../MMM/MMMUtils";
import { formatNumber } from "../../utils/format-utils";
import { useCompanyInfo } from "../../redux/company";

// TODO: Fetch values file name
const LAST_REFRESH_DATE = {
  tinuititest: "2025-03-20",
  olly: "2025-04-22",
  dsw: "2025-08-05",
  onceuponafarm: "2025-08-04",
  milanlaser: "2025-08-08",
};

interface SummaryMetricsObj {
  [metric: string]: string | number;
}

interface BusinessUnderstandingProps {
  groupBy: string;
  outcomeVariable: "revenue" | "kpiVolume";
  summaryMetricsRows: any[];
  mapeByGeoRows: any[];
  fitRows: any[];
  mediaSpendRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  geoNames: { [dma: string]: string };
}

const GEO_MAP_COLORS = ["#7FD800", "#7ed600d6", "#7ed60073", "#7ed6004d", "#7ed60033", "#7ed6001a"];

export const BusinessUnderstanding: React.FC<BusinessUnderstandingProps> = ({
  groupBy,
  outcomeVariable,
  summaryMetricsRows,
  mapeByGeoRows,
  fitRows,
  mediaSpendRows,
  channelBreakoutMap,
  geoNames,
}) => {
  const { cid: company } = useCompanyInfo();
  const [chartDateGrouping, setChartDateGrouping] = useState("Week");
  const [selectedView, setSelectedView] = useState<string>("by DMA");
  const [selectedDMA, setSelectedDMA] = useState<string>("");
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  const { summaryMetricsData, mapeByGeoData, fitData, mediaSpendData } = useMemo(() => {
    if (
      R.isEmpty(summaryMetricsRows) ||
      R.isEmpty(mapeByGeoRows) ||
      R.isEmpty(fitRows) ||
      R.isEmpty(mediaSpendRows)
    ) {
      return {
        summaryMetricsData: {},
        mapeByGeoData: {},
        fitData: [],
        mediaSpendData: [],
        channelBreakoutMap: {},
      };
    }

    // SUMMARY METRICS
    let summaryMetricsMap: SummaryMetricsObj = {};
    summaryMetricsRows.forEach(row => {
      summaryMetricsMap[row.metric] = row.value;
    });
    const summaryMetrics: SummaryMetrics = {
      firstModelObservation: String(summaryMetricsMap["Start Date"]),
      lastModelObservation: String(summaryMetricsMap["End Date"]),
      outcomeVariable: String(summaryMetricsMap.Outcome),
      averageWeeklySpend: String(summaryMetricsMap["Average Weekly Spend"]),
      lastRefreshDate: LAST_REFRESH_DATE[company],
      trainTestSplit: percentageToSplitFraction(String(summaryMetricsMap["Holdout Percentage"])),
      wMAPE: String(summaryMetricsMap.wMAPE),
      overallROAS: String(summaryMetricsMap["Total ROI"]),
    };

    /**
     * Filter out rows that do not have a dma name in geoNames map
     * TODO: Revisit this logic later.
     */

    // MAPE BY GEO
    let mapeByGeo: MAPEByGeo = {};
    mapeByGeoRows.forEach(row => {
      const dma = row.geo.split("_")[1];
      if (geoNames[dma]) {
        mapeByGeo[dma] = row.mape;
      }
    });

    // FIT
    let fit: Fit[] = [];
    fitRows.forEach(row => {
      const { geo, time: date, metric, expected, baseline, actual } = row;
      const dma = geo.split("_")[1];
      if (metric === "mean" && geoNames[dma]) {
        const parsedDate = new Date(Date.parse(date));
        fit.push({
          geo: dma,
          date: Dfns.format("yyyy-MM-dd", parsedDate), // Temporary fix for Morgan's fake data where date is not formatted as string
          expected,
          baseline,
          actual,
        });
      }
    });

    // MEDIA SPEND
    let mediaSpend: any[] = [];
    mediaSpendRows.forEach(row => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __EMPTY, geo, time: date, ...rest } = row;
      const dma = geo.split("_")[1];
      if (geoNames[dma]) {
        const parsedDate = new Date(Date.parse(date));
        mediaSpend.push({
          geo: dma,
          date: Dfns.format("yyyy-MM-dd", parsedDate), // Temporary fix for Morgan's fake data where date is not formatted as string
          ...rest,
        });
      }
    });

    return {
      summaryMetricsData: summaryMetrics,
      mapeByGeoData: mapeByGeo,
      fitData: fit,
      mediaSpendData: mediaSpend,
    };
  }, [company, fitRows, geoNames, mapeByGeoRows, mediaSpendRows, summaryMetricsRows]);

  const modelOverviewDataToExport: [string[], (string | number)[]] = useMemo(() => {
    if (!R.isEmpty(summaryMetricsData)) {
      const keyToPrettyName: {
        dataKey: string;
        name: string;
        value: string | number;
      }[] = [
        {
          dataKey: "firstModelObservation",
          name: "First model observation",
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.firstModelObservation ?? ""),
        },
        {
          dataKey: "lastModelObservation",
          name: "Last model observation",
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastModelObservation ?? ""),
        },
        {
          dataKey: "averageWeeklySpend",
          name: "Average weekly spend",
          value: (summaryMetricsData as SummaryMetrics)?.averageWeeklySpend.split(".")[0] ?? "",
        },
        {
          dataKey: "outcomeVariable",
          name: "Outcome variable",
          value: (summaryMetricsData as SummaryMetrics)?.outcomeVariable ?? "",
        },
        {
          dataKey: "lastRefreshDate",
          name: "Last refresh date",
          value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastRefreshDate ?? ""),
        },
        {
          dataKey: "trainTestSplit",
          name: "Train/Test split",
          value: (summaryMetricsData as SummaryMetrics)?.trainTestSplit ?? "",
        },
        {
          dataKey: "wMAPE",
          name: "Out-of-sample MAPE",
          value: (summaryMetricsData as SummaryMetrics)?.wMAPE ?? "",
        },
        {
          dataKey: "overallROAS",
          name: "Overall ROAS",
          value: (summaryMetricsData as SummaryMetrics)?.overallROAS.substring(1) ?? "",
        },
      ];

      return keyToPrettyName.reduce(
        (acc, cv) => {
          acc[0].push(cv.name);
          acc[1].push(cv.value);
          return acc;
        },
        [[], []] as [string[], (string | number)[]]
      );
    } else {
      return [[], []];
    }
  }, [summaryMetricsData]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const csvDownloadModelOverview = useCallback(() => {
    downloadJSONToCSV(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const actualVsPredictedData = useMemo(() => {
    let rows: {
      date: string;
      expected: number;
      actual: number;
    }[] = [];
    if (fitData) {
      const filteredData = selectedDMA ? fitData.filter(item => item.geo === selectedDMA) : fitData;

      let totalsByDate: Record<string, Record<string, number>> = {};
      for (let item of filteredData) {
        const { date, actual, expected } = item;

        const dateToUse = getGroupingValue(date, chartDateGrouping as DateGroupingKey, fiscalCal);
        totalsByDate = R.mergeDeepRight(totalsByDate, {
          [dateToUse]: {
            actual: R.pathOr(0, [dateToUse, "actual"], totalsByDate) + actual,
            expected: R.pathOr(0, [dateToUse, "expected"], totalsByDate) + expected,
          },
        });
      }

      Object.keys(totalsByDate).forEach(date => {
        rows.push({
          date,
          actual: totalsByDate[date].actual,
          expected: totalsByDate[date].expected,
        });
      });
    }

    return rows;
  }, [fitData, chartDateGrouping, selectedDMA, fiscalCal]);

  const spendChartData = useMemo(() => {
    let rows: {
      date: string;
      spend: number;
    }[] = [];
    if (mediaSpendData && selectedView === "by DMA") {
      const filteredData = selectedDMA
        ? mediaSpendData.filter(item => item.geo === selectedDMA)
        : mediaSpendData;

      let totalsByDate: Record<string, Record<string, number>> = {};
      for (let item of filteredData) {
        const { geo, date, ...rest } = item;

        const dateToUse = getGroupingValue(date, chartDateGrouping as DateGroupingKey, fiscalCal);
        const spend = R.sum(Object.values(rest));
        totalsByDate = R.mergeDeepRight(totalsByDate, {
          [dateToUse]: {
            spend: R.pathOr(0, [dateToUse, "spend"], totalsByDate) + spend,
          },
        });
      }

      Object.keys(totalsByDate).forEach(date => {
        rows.push({
          date,
          spend: totalsByDate[date].spend,
        });
      });
    }

    return rows;
  }, [mediaSpendData, chartDateGrouping, selectedView, selectedDMA, fiscalCal]);

  const areaChartData = useMemo(() => {
    let rows: {
      newD: any[];
      areas: any;
      groupedAreas: any;
    } = {
      newD: [],
      areas: [],
      groupedAreas: [],
    };
    if (mediaSpendData && channelBreakoutMap && selectedView === "Over Time") {
      let dataToProcess;
      dataToProcess = R.filter(R.compose(R.not, R.propEq("name", "Covariates")))(mediaSpendData);

      let formattedData: {
        date: string;
        value: number;
        name: string;
        channel: string;
        platform: string;
        tactic: string;
        brand_nonbrand: string;
      }[] = [];
      /** Want to normalize a wide-format dataset (one row with many breakout columns) into a long-format array,
       *  where each channel breakout becomes its own row
       */
      dataToProcess.forEach(entry => {
        const { geo, date, ...rest } = entry;
        Object.keys(rest).forEach(key => {
          const breakoutValues = channelBreakoutMap[key.toLowerCase()];
          if (breakoutValues) {
            const { channel, platform, tactic, brand_nonbrand } = breakoutValues;
            formattedData.push({
              date,
              value: rest[key],
              name: key,
              channel,
              platform,
              tactic,
              brand_nonbrand,
            });
          }
        });
      });

      rows = cptbAreaData({
        data: formattedData,
        data1Key: "value",
        groupBy,
        dateGrouping: chartDateGrouping,
        fiscalCal,
      });
    }

    return rows;
  }, [mediaSpendData, chartDateGrouping, selectedView, channelBreakoutMap, groupBy, fiscalCal]);

  const mapSvgRef = useRef<SVGSVGElement | null>(null);

  const { renderColor, renderToolTip } = useMemo(() => {
    if (mapeByGeoData && !R.isEmpty(R.keys(geoNames))) {
      const thresholds = getThresholdBinsWithStdDevNonAdjusted(Object.values(mapeByGeoData));

      const renderColor = (dma_code: string) => {
        const mapeValues = Object.values(mapeByGeoData);
        const minValue = Math.min(...mapeValues);
        const maxValue = Math.max(...mapeValues);
        const range = maxValue - minValue;

        // Get the MAPE value for the given DMA code
        const mape = mapeByGeoData[dma_code];

        // If no MAPE value or if all values are the same, return the first color
        if (mape === undefined || range === 0) {
          return Brand20;
        }

        return getColorForIndexedVolume(mape, thresholds, GEO_MAP_COLORS);
      };

      const renderToolTip = (dma_code: string) => {
        if (!mapeByGeoData[dma_code]) {
          return null;
        }

        const mape = mapeByGeoData[dma_code];

        return (
          <div className="geoMapTooltip">
            <div className="geoMapTooltipHeader">{`${
              geoNames[dma_code] ?? "N/A"
            } (DMA Code: ${dma_code})`}</div>
            <div className="geoMapTooltipBody">
              <div className="fieldName">{"MAPE "}</div>
              <div className="fieldValue">{mape ? `${formatNumber(mape, 2)}%` : "N/A"}</div>
            </div>
          </div>
        );
      };

      return {
        renderColor,
        renderToolTip,
      };
    } else {
      return {
        renderColor: () => Brand20,
        renderToolTip: () => "No info for this DMA",
      };
    }
  }, [geoNames, mapeByGeoData]);

  const modelOverviewMape = parseFloat((summaryMetricsData as SummaryMetrics)?.wMAPE);
  const modelOverview = useMemo(() => {
    return (
      <DynamicModelOverview
        sections={[
          {
            title: "Training Data",
            rows: [
              {
                label: "First model observation",
                value: dateToMddyy(
                  (summaryMetricsData as SummaryMetrics)?.firstModelObservation ?? ""
                ),
              },
              {
                label: "Last model observation",
                value: dateToMddyy(
                  (summaryMetricsData as SummaryMetrics)?.lastModelObservation ?? ""
                ),
              },
              {
                label: "Average weekly spend",
                value: (summaryMetricsData as SummaryMetrics)?.averageWeeklySpend.split(".")[0],
              },
              {
                label: "Outcome variable",
                value: (summaryMetricsData as SummaryMetrics)?.outcomeVariable,
              },
            ],
          },
          {
            title: "Recency",
            rows: [
              {
                label: "Last refresh date",
                value: dateToMddyy((summaryMetricsData as SummaryMetrics)?.lastRefreshDate ?? ""),
              },
            ],
          },
          {
            title: "Statistics",
            rows: [
              {
                label: "Train/Test split",
                value: (summaryMetricsData as SummaryMetrics)?.trainTestSplit,
                tooltipText:
                  "This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.",
              },
              {
                label: "Out-of-sample MAPE",
                value: (
                  <>
                    {modelOverviewMape <= 20 && <MdDone className="greenCheck" size={22} />}
                    {modelOverviewMape}
                  </>
                ),
                tooltipText:
                  "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual)).",
              },
            ],
          },
          {
            title: "Key Results",
            rows: [
              {
                label: "Overall ROAS",
                value: (summaryMetricsData as SummaryMetrics)?.overallROAS.substring(1),
              },
            ],
          },
        ]}
        rightActions={
          <DownloadDropdown
            size="sm"
            onClickOptions={[excelDownloadModelOverview, csvDownloadModelOverview]}
            menuOptions={["XLSX", "CSV"]}
          />
        }
        height={674}
        loading={false}
      />
    );
  }, [summaryMetricsData, modelOverviewMape, excelDownloadModelOverview, csvDownloadModelOverview]);

  const mapeByGeoExportData = useMemo(() => {
    if (!R.isEmpty(mapeByGeoData) && !R.isEmpty(geoNames)) {
      return Object.entries(mapeByGeoData).map(([dma, mape]) => ({
        dma,
        name: geoNames[dma] || "N/A",
        mape: mape ? `${formatNumber(mape, 2)}%` : "N/A",
      }));
    }
    return [];
  }, [mapeByGeoData, geoNames]);

  const excelDownloadModelFitByDma = useCallback(() => {
    exportToExcel(mapeByGeoExportData, `${company}_model_fit_by_dma`);
  }, [mapeByGeoExportData, company]);

  const pngDownloadModelFitByDma = useCallback(() => {
    downloadPNG(".geoMapAndGradient", `${company}_model_fit_by_dma`);
  }, [company]);

  const chartsContent = useMemo(() => {
    const modelViewByDma = (
      <>
        <ChartContainer
          enableHoverDesign
          rightActions={
            <DownloadDropdown
              size="sm"
              onClickOptions={[excelDownloadModelFitByDma, pngDownloadModelFitByDma]}
            />
          }
          title="Model Fit by DMA"
          viewRightActions={true}
        >
          <div className="geoMapAndGradient">
            <div className="geoMap">
              <AutoSizer>
                {({ width, height }) => (
                  <DmaMap
                    width={width}
                    height={height}
                    selection={selectedDMA}
                    onClick={dma => {
                      if (selectedDMA === dma) {
                        setSelectedDMA("");
                      } else {
                        setSelectedDMA(dma);
                      }
                    }}
                    renderColor={renderColor}
                    renderTooltip={renderToolTip}
                    ref={mapSvgRef}
                    borderColor="ffffff"
                  />
                )}
              </AutoSizer>
            </div>
            <div className="geoMapAndGradientLegendHolder">
              <ModelAccuracyGradient />
            </div>
          </div>
        </ChartContainer>
      </>
    );

    const actualVsPredictedChart = (
      <>
        <ChartContainer
          enableHoverDesign
          title={
            <>
              <Dropdown
                type={DropdownToggleType.WIDGET_TITLE}
                value={chartDateGrouping}
                options={DATE_GROUPING_OPTIONS}
                onChange={option => setChartDateGrouping(option)}
              />
              <div className="headerLeft">{`Historical ${OUTCOME_TYPE_STRING[outcomeVariable]}${
                selectedDMA ? ` in ${geoNames[selectedDMA]}` : ""
              }`}</div>
              <div className="headerRight">(Actual vs Predicted)</div>
            </>
          }
        >
          {!R.isEmpty(actualVsPredictedData) && (
            <ActualVsPredicted
              data={actualVsPredictedData}
              dateGrouping={chartDateGrouping}
              legendValue={OUTCOME_TYPE_STRING[outcomeVariable]}
              syncId={1}
              tooltipFormatter={val => `${OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 2)}`}
              yAxisFormatter={val => `${OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0)}`}
            />
          )}
        </ChartContainer>
      </>
    );

    const spendChart = (
      <>
        <ChartContainer
          enableHoverDesign
          title={
            <>
              <Dropdown
                size="sm"
                type={DropdownToggleType.WIDGET_TITLE}
                value={chartDateGrouping}
                options={DATE_GROUPING_OPTIONS}
                onChange={option => setChartDateGrouping(option)}
              />
              <div className="headerLeft">{`Historical Spend${
                selectedView === "by DMA" && selectedDMA ? ` in ${geoNames[selectedDMA]}` : ""
              }`}</div>
            </>
          }
        >
          {!R.isEmpty(spendChartData) && selectedView === "by DMA" && (
            <Spend
              data={spendChartData}
              dateGrouping={chartDateGrouping}
              syncId={1}
              tooltipFormatter={val => `${currencyFormatter.format(val, 2)}`}
              yAxisFormatter={val => `${currencyFormatter.format(val, 0)}`}
            />
          )}
          {!R.isEmpty(areaChartData) && selectedView === "Over Time" && (
            <AreaChart
              areas={areaChartData.areas}
              data={areaChartData.newD}
              dateGrouping={chartDateGrouping}
              groupedAreas={areaChartData.groupedAreas}
              incrementality={false}
              syncId={1}
              tooltipFormatter={val => {
                return val < 1000
                  ? currencyFormatter.format(val, 0)
                  : currencyFormatter.format(val, 1);
              }}
              xAxisDataKey="date"
              xAxisTickFormatter={val => dateToMddyy(val)}
              yAxisTickFormatter={val => `${currencyFormatter.format(val, 0)}`}
            />
          )}
        </ChartContainer>
      </>
    );

    return selectedView === "by DMA" ? (
      <div className="byDmaContent">
        <div className="topRow">
          {modelViewByDma}
          <div className="right">{modelOverview}</div>
        </div>
        {actualVsPredictedChart}
        {spendChart}
      </div>
    ) : (
      <div className="overTimeContent">
        <div>{actualVsPredictedChart}</div>
        {spendChart}
      </div>
    );
  }, [
    excelDownloadModelFitByDma,
    pngDownloadModelFitByDma,
    chartDateGrouping,
    outcomeVariable,
    selectedDMA,
    geoNames,
    actualVsPredictedData,
    selectedView,
    spendChartData,
    areaChartData,
    modelOverview,
    renderColor,
    renderToolTip,
  ]);

  return (
    <div className="businessUnderstandingWidget">
      <WidgetContainer
        collapsible
        header={
          <div className="buHeader">
            <span>How well does the model understand our business</span>
            <Dropdown
              className="largeTitle"
              onChange={selection => {
                if (selection === "Over Time") {
                  setSelectedDMA("");
                }
                setSelectedView(selection);
              }}
              options={["by DMA", "Over Time"]}
              type={DropdownToggleType.WIDGET_TITLE}
              value={selectedView}
            />
            <span>?</span>
          </div>
        }
        subHeader={
          <>
            High out-of-sample prediction accuracy is indicative of reliable causal understanding.
            <InfoTooltip rightLabel="More info" size="sm">
              The model is estimated by splitting up the time series data into slices called "train"
              and "test." The training data is used to estimate the relationships between inputs
              (media investments, contextual variables) and outputs (the KPI). The test data is used
              to evaluate the performance of the trained model "out of sample," i.e. on observations
              it has not already encountered. Out-of-sample predictive accuracy is a critical
              concept because it proxies what we ultimately care about, namely an understanding of
              likely KPI outputs given a combination of novel media inputs. See the out-of-sample
              statistics at right for measures of this concept. We provide these metrics at the DMA
              level so we can understand how the model fits for each geographic unit.
            </InfoTooltip>
          </>
        }
      >
        <div className="businessUnderstandingContainer">
          <div className="left">{chartsContent}</div>
          {selectedView === "Over Time" && <div className="right">{modelOverview}</div>}
        </div>
      </WidgetContainer>
    </div>
  );
};

export default BusinessUnderstanding;
